import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.jsx';
import { Button } from '../ui/button.jsx';
import { Badge } from '../ui/badge.jsx';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessPlan } from '../../App';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  MousePointer, 
  Target,
  Calendar,
  ExternalLink,
  Copy,
  BarChart3,
  Award,
  XCircle,
  Building2,
  ArrowRight
} from 'lucide-react';

const AffiliateDashboard = () => {
  const { user } = useAuth();
  const { userType } = useBusinessPlan();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [affiliate, setAffiliate] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchAnalytics();
  }, []);

  const setupTestData = async () => {
    try {
      const token = Cookies.get('enakid_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/setup-test-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Test data created! You now have $2,800 available for payout.');
        fetchDashboardData();
        fetchAnalytics();
      } else {
        toast.error('Failed to setup test data');
      }
    } catch (error) {
      console.error('Error setting up test data:', error);
      toast.error('Error setting up test data');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = Cookies.get('enakid_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Dashboard API response:', data);
        console.log('Affiliate data from response:', data.affiliate);
        setDashboardData(data);
        setAffiliate(data.affiliate);
        console.log('Affiliate state should be set now');
      } else if (response.status === 404) {
        // User is not an affiliate yet - this is expected
        console.log('404 response - user is not an affiliate');
        setAffiliate(null);
        setDashboardData(null);
      } else {
        console.log('Non-404 error response:', response.status);
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      // Don't show error toast for 404 - user simply isn't an affiliate yet
      if (!error.message.includes('404') && !error.message.includes('Not Found')) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = Cookies.get('enakid_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/analytics?period_days=30`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else if (response.status === 404) {
        // User is not an affiliate yet - this is expected
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Don't show error for non-affiliates
    }
  };

  const copyAffiliateCode = () => {
    if (affiliate?.affiliate_code) {
      navigator.clipboard.writeText(affiliate.affiliate_code);
      toast.success('Affiliate code copied to clipboard!');
    }
  };

  const copyAffiliateLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Affiliate link copied to clipboard!');
  };

  const generateAffiliateLink = (targetPage) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}${targetPage}?ref=${affiliate?.affiliate_code}`;
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'bg-amber-100 text-amber-800 border-amber-200',
      silver: 'bg-gray-100 text-gray-800 border-gray-200', 
      gold: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      platinum: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[tier] || colors.bronze;
  };

  const getCommissionRate = (tier) => {
    const rates = {
      bronze: '20%',
      silver: '25%',
      gold: '30%',
      platinum: '35%'
    };
    return rates[tier] || '20%';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Affiliate Profile</h3>
        <p className="text-gray-500 mb-4">You need to register as an affiliate first.</p>
        <Button onClick={() => window.location.href = '/affiliate/register'}>
          Become an Affiliate
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Dashboard</h1>
          <p className="text-gray-500">Track your referrals and earnings</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`${getTierColor(affiliate.tier)} font-medium`}>
            <Award className="h-4 w-4 mr-1" />
            {affiliate.tier.charAt(0).toUpperCase() + affiliate.tier.slice(1)} Tier
          </Badge>
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
            <span className="text-sm font-medium">Code: {affiliate.affiliate_code}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={copyAffiliateCode}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Status Alert */}
      {affiliate.status === 'pending' && (
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Calendar className="h-6 w-6 text-yellow-500" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-base font-semibold text-yellow-900">
                Application Under Review
              </h3>
              <p className="text-sm text-yellow-800 mt-1">
                Your affiliate application is being reviewed by our team. You'll receive an email notification once approved. 
                In the meantime, you can view this dashboard, but referral tracking will only start after approval.
              </p>
            </div>
          </div>
        </div>
      )}

      {affiliate.status === 'approved' && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Award className="h-6 w-6 text-green-500" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-base font-semibold text-green-900">
                ✓ Affiliate Status: Active
              </h3>
              <p className="text-sm text-green-800 mt-1">
                Your affiliate account is active! Start sharing your referral links below to earn commissions on every successful referral.
              </p>
            </div>
          </div>
        </div>
      )}

      {affiliate.status === 'rejected' && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <XCircle className="h-6 w-6 text-red-500" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-base font-semibold text-red-900">
                Application Rejected
              </h3>
              <p className="text-sm text-red-800 mt-1">
                Unfortunately, your affiliate application was not approved at this time. Please contact support for more information.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Business Plan CTA for Affiliate-Only Users */}
      {userType === 'affiliateOnly' && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Building2 className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className="text-lg font-bold text-gray-900">
                    Expand to Business Planning
                  </h3>
                </div>
                <p className="text-sm text-gray-700 mb-4">
                  Want to create comprehensive financial models for your business? Add business planning capabilities to your account and access our full suite of financial tools.
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li className="flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2 text-blue-600" />
                    Financial projections & forecasting
                  </li>
                  <li className="flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2 text-blue-600" />
                    Revenue & expense planning
                  </li>
                  <li className="flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2 text-blue-600" />
                    Performance analytics & reporting
                  </li>
                </ul>
              </div>
              <div className="ml-6">
                <Button 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-semibold"
                  onClick={() => {
                    // Navigation component handles this action
                    // User can click "Create Business Plan" in sidebar
                    toast.info('Use the "Create Business Plan" button in the sidebar navigation →');
                  }}
                >
                  <Building2 className="h-5 w-5 mr-2" />
                  Create Business Plan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clicks</CardTitle>
            <MousePointer className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.total_clicks || 0}</div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversions</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.conversions || 0}</div>
            <p className="text-xs text-muted-foreground">
              {analytics?.conversion_rate || 0}% conversion rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(dashboardData?.monthly_earnings)}
            </div>
            <p className="text-xs text-muted-foreground">
              Commission rate: {getCommissionRate(affiliate.tier)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(affiliate.lifetime_earnings)}
            </div>
            <p className="text-xs text-muted-foreground">
              Current balance: {formatCurrency(affiliate.current_balance)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Affiliate Links Section */}
      <Card id="affiliate-links">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <ExternalLink className="h-5 w-5 mr-2 text-blue-600" />
            Your Affiliate Links
          </CardTitle>
          <CardDescription>
            Share these links to track referrals and earn commissions. Each link includes your unique affiliate code.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Affiliate Code Display */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">Your Affiliate Code</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">{affiliate.affiliate_code}</p>
                </div>
                <Button 
                  onClick={copyAffiliateCode}
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-100"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Code
                </Button>
              </div>
            </div>

            {/* Pre-generated Links */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Quick Share Links</p>
              
              {/* Pricing Page Link */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Pricing Page</p>
                  <p className="text-xs text-gray-500 font-mono break-all">
                    {generateAffiliateLink('/pricing')}
                  </p>
                </div>
                <Button 
                  onClick={() => copyAffiliateLink(generateAffiliateLink('/pricing'))}
                  size="sm"
                  variant="ghost"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              {/* Signup Page Link */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Sign Up Page</p>
                  <p className="text-xs text-gray-500 font-mono break-all">
                    {generateAffiliateLink('/signup')}
                  </p>
                </div>
                <Button 
                  onClick={() => copyAffiliateLink(generateAffiliateLink('/signup'))}
                  size="sm"
                  variant="ghost"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              {/* Home Page Link */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Home Page</p>
                  <p className="text-xs text-gray-500 font-mono break-all">
                    {generateAffiliateLink('/')}
                  </p>
                </div>
                <Button 
                  onClick={() => copyAffiliateLink(generateAffiliateLink('/'))}
                  size="sm"
                  variant="ghost"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* How to Use Instructions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-medium text-amber-900 mb-2">💡 How to Use Your Links</p>
              <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                <li>Share these links on social media, email, or your website</li>
                <li>When someone clicks your link and signs up, you'll earn a commission</li>
                <li>Track your clicks and conversions in real-time on this dashboard</li>
                <li>Your unique code ({affiliate.affiliate_code}) is automatically included in each link</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              className="w-full justify-start bg-green-600 hover:bg-green-700 text-white" 
              onClick={() => navigate('/affiliate/payouts')}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Request Payout
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => window.scrollTo({ top: document.getElementById('affiliate-links')?.offsetTop - 100, behavior: 'smooth' })}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Referral Links
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              View Analytics Above
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => window.scrollTo({ top: document.getElementById('recent-referrals')?.offsetTop - 100, behavior: 'smooth' })}
            >
              <Users className="h-4 w-4 mr-2" />
              View Recent Referrals
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Performance Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Average Order Value</span>
              <span className="font-medium">{formatCurrency(analytics?.average_order_value)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Customers</span>
              <span className="font-medium">{analytics?.new_customers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pending Commissions</span>
              <span className="font-medium text-yellow-600">
                {formatCurrency(analytics?.pending_commissions)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tier Progress</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.next_tier_threshold ? (
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>Progress to next tier</span>
                  <span>{affiliate.total_conversions}/{dashboardData.next_tier_threshold}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{
                      width: `${Math.min((affiliate.total_conversions / dashboardData.next_tier_threshold) * 100, 100)}%`
                    }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">
                  {dashboardData.next_tier_threshold - affiliate.total_conversions} more conversions to unlock higher commission rates
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <Award className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-purple-600">Platinum Tier Achieved!</p>
                <p className="text-xs text-gray-500">You're at the highest tier</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Clicks</CardTitle>
            <CardDescription>Latest traffic from your referral links</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_clicks?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_clicks.slice(0, 5).map((click, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium">Click tracked</p>
                        <p className="text-xs text-gray-500">
                          {click.clicked_at ? new Date(click.clicked_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Just now'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {click.utm_campaign || 'Direct'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <MousePointer className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No recent clicks</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card id="recent-referrals">
          <CardHeader>
            <CardTitle className="text-lg">Recent Referrals</CardTitle>
            <CardDescription>Latest conversions from your efforts</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_referrals?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_referrals.slice(0, 5).map((referral, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${
                        referral.status === 'converted' ? 'bg-green-400' : 
                        referral.status === 'registered' ? 'bg-yellow-400' : 'bg-gray-400'
                      }`}></div>
                      <div>
                        <p className="text-sm font-medium">
                          {referral.user_email}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge 
                        variant={referral.status === 'converted' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {referral.status}
                      </Badge>
                      {referral.commission_earned > 0 && (
                        <p className="text-xs text-green-600 font-medium">
                          {formatCurrency(referral.commission_earned)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No referrals yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateDashboard;